import {
  Grid,
  CardContent,
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  Card,
  Box,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Page from '../components/Page';

const TermsOfUse: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Page title='Terms of Service'>
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          mb: 5,
        }}
      >
        <Grid container justifyContent='center' sx={{ mb: 3 }}>
          <Grid mt={1}>
            <img
              src='/static/images/one_line_logo.png'
              alt='logo'
              style={{
                width: '300px',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            />
          </Grid>
        </Grid>

        <Grid container justifyContent='center' sx={{ mb: 3 }}>
          <Grid>
            <Card
              sx={{
                maxWidth: '900px',
                position: 'relative',
                borderRadius: '8px',
                padding: '1px',

                overflow: 'hidden',
              }}
            >
              <CardContent
                sx={{
                  borderRadius: 'inherit',
                  background: '#E0F7FF',
                  p: 4,
                  pb: '12px !important',
                }}
              >
                <Typography
                  fontSize='23px'
                  fontWeight='bold'
                  gutterBottom
                  mt={1}
                >
                  QR BUDDY TERMS OF USE
                </Typography>

                <Typography fontSize='13px' fontWeight='bold' gutterBottom>
                  Last Updated: October 8, 2025
                </Typography>

                <Typography fontSize='18px' fontWeight='bold' gutterBottom>
                  AGREEMENT TO TERMS
                </Typography>
                <Typography fontSize='14px' paragraph>
                  These Terms of Use constitute a legally binding agreement made
                  between you, whether personally as an end user of QR Buddy
                  (“User”) or on behalf of an entity purchasing leads through QR
                  Buddy (“Customer”), and KnoCard LLC (“Company,” “we,” “us,” or
                  “our”), concerning your access to and use of the{' '}
                  <b>
                    QR Buddy website, mobile app, QR code scanning technology,
                    lead services, and related features{' '}
                  </b>
                  (collectively, the “Services”).
                </Typography>
                <Typography fontSize='14px' paragraph>
                  By accessing or using QR Buddy, you acknowledge that you have
                  read, understood, and agreed to be bound by these Terms. IF
                  YOU DO NOT AGREE, YOU MUST NOT USE THE SERVICES.
                </Typography>
                <Typography fontSize='14px' paragraph>
                  We may update these Terms from time to time. Updates will be
                  reflected by the “Last Updated” date, and your continued use
                  after such updates constitutes acceptance.
                </Typography>
                <Typography fontSize='14px' paragraph>
                  The Services are intended for users who are at least 18 years
                  old. Minors are not permitted to register for or use QR Buddy.
                </Typography>
                <Typography fontSize='18px' fontWeight='bold' gutterBottom>
                  HOW QR BUDDY WORKS
                </Typography>

                <Typography variant='body1' gutterBottom>
                  <b>For Users (Consumers):</b>&nbsp;QR Buddy provides a free
                  digital tool when you scan a QR Buddy code or sales sticker.
                  You may:
                </Typography>
                <List sx={{ listStyleType: 'disc', pl: 4 }}>
                  <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary='Save product information related to your scans.' />
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary='Access additional details and materials about products and services you are interested in.' />
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary='Receive AI-powered responses to common product questions.' />
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary='Never be charged for using QR Buddy.' />
                  </ListItem>
                </List>

                <Typography fontSize='18px' fontWeight='bold' gutterBottom>
                  INTELLECTUAL PROPERTY RIGHTS
                </Typography>
                <Typography fontSize='14px' paragraph>
                  All QR Buddy software, databases, website designs, code, AI
                  tools, and related content (“Content”) are owned or licensed
                  by KnoCard LLC and protected under U.S. and international
                  intellectual property laws.
                </Typography>
                <Typography fontSize='14px' paragraph>
                  You are granted a limited license to access and use QR Buddy
                  for its intended purpose (scanning, saving product info,
                  purchasing leads). You may not copy, resell, reverse engineer,
                  or otherwise exploit QR Buddy for unauthorized commercial use.
                </Typography>
                <Typography
                  variant='h6'
                  component='h2'
                  sx={{
                    fontWeight: 'bold',
                    mb: 2,
                    textAlign: { xs: 'center', sm: 'left' },
                  }}
                >
                  USER RESPONSIBILITIES
                </Typography>

                <Typography variant='body1' gutterBottom>
                  By using QR Buddy, you represent and warrant that:
                </Typography>

                <List sx={{ listStyleType: 'decimal', pl: 4 }}>
                  <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary='Any information you provide is true, accurate, and complete.' />
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary='You will not use QR Buddy for unlawful purposes (spam, fraud, harassment, etc.).' />
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary='You will not interfere with or disrupt the Services.' />
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary='If you are a Customer, you have the authority to bind your business and comply with these Terms.' />
                  </ListItem>
                </List>

                <Typography fontSize='18px' fontWeight='bold' gutterBottom>
                  PROHIBITED ACTIVITIES
                </Typography>

                <Typography variant='body1' gutterBottom>
                  You agree not to:
                </Typography>
                <List sx={{ listStyleType: 'disc', pl: 4 }}>
                  <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary='Create fake QR Buddy accounts or scan codes under false pretenses.' />
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary='Attempt to rebrand, sublicense, or resell QR Buddy leads or software.' />
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary='Reverse engineer or otherwise misuse QR Buddy technology.' />
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary='Upload or transmit viruses, spyware, or malicious code.' />
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary='Use QR Buddy in violation of any marketing, privacy, or consumer protection laws.' />
                  </ListItem>
                </List>
                <Typography fontSize='18px' fontWeight='bold' gutterBottom>
                  DATA COLLECTION & PRIVACY
                </Typography>
                <List sx={{ listStyleType: 'disc', pl: 4 }}>
                  <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText
                      primary={
                        <Typography component='span'>
                          <strong>Users:</strong> When you scan a QR Buddy code,
                          we collect limited personal information (e.g., name,
                          email, phone number) to deliver the lead to the
                          appropriate Customer. Your information may also be
                          used to provide you with personalized content, AI
                          assistance, or product details. You will never be
                          billed for use of QR Buddy.
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
                <Typography component='span'>
                  We will not share/sell your data to any third party for
                  marketing and promotional purposes.
                </Typography>

                <Typography
                  fontSize='18px'
                  fontWeight='bold'
                  marginTop={1.2}
                  gutterBottom
                >
                  DISCLAIMER OF WARRANTIES
                </Typography>
                <Typography fontSize='14px' paragraph>
                  QR Buddy is provided <b>“AS IS” and “AS AVAILABLE.”</b>{' '}
                  KnoCard LLC makes no warranties regarding the accuracy of
                  leads, AI responses, or the suitability of QR Buddy for your
                  intended business results.
                </Typography>
                <Typography fontSize='18px' fontWeight='bold' gutterBottom>
                  LIMITATION OF LIABILITY
                </Typography>
                <Typography fontSize='14px' paragraph>
                  To the fullest extent permitted by law, KnoCard LLC shall not
                  be liable for any damages, including but not limited to lost
                  profits, loss of data, or business interruption, arising out
                  of your use of QR Buddy.
                </Typography>
                <Typography fontSize='18px' fontWeight='bold' gutterBottom>
                  TERMINATION
                </Typography>
                <Typography fontSize='14px' paragraph>
                  We may suspend or terminate access to QR Buddy at any time,
                  without notice, if you violate these Terms or engage in misuse
                  of the Services.
                </Typography>

                <Typography fontSize='18px' fontWeight='bold' gutterBottom>
                  GOVERNING LAW
                </Typography>
                <Typography fontSize='14px' paragraph>
                  These Terms are governed by and construed under the laws of
                  the State of Ohio, without regard to conflict of laws.
                </Typography>

                <Typography fontSize='18px' fontWeight='bold' gutterBottom>
                  CONTACT US
                </Typography>
                <Typography fontSize='14px' paragraph>
                  For questions about these Terms or the QR Buddy Services,
                  contact us at: <br />
                  <Box
                    component={'a'}
                    href='mailto:support@knocard.com'
                    sx={{ textDecoration: 'underline', color: 'primary.main' }}
                  >
                    support@knocard.com
                  </Box>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
};
export default TermsOfUse;